import * as yup from "yup"
import type {
    PvEtudeEcoFullForm,
    PvEtudeEcoPlageHoraire,
    PvEtudeEcoSubscribeForm,
} from "@modules/pv/types/pv-etude-eco-form.types"
import {
    isValidAwsString,
    isValidDecimalPlaces,
    isValidNumericField,
} from "@modules/pv/utils/pv-etude-eco-form"

/**
 * Libellés d’erreur pour le schéma Yup du formulaire étude éco PV.
 */
export type PvEtudeEcoFormSchemaMessages = {
    nameRequired: string
    nameInvalid: string
    numericRequired: string
    decimalsInvalid: string
    plageStartRequired: string
    plageEndRequired: string
    plageBlockInvalid: string
}

type CreateFieldError = (path: string, message: string) => yup.ValidationError

function collectNumericObjectErrors(
    obj: Record<string, unknown> | undefined,
    pathPrefix: string,
    createError: CreateFieldError,
    m: PvEtudeEcoFormSchemaMessages
): yup.ValidationError[] {
    const out: yup.ValidationError[] = []
    if (!obj) return out
    for (const [key, value] of Object.entries(obj)) {
        if (isValidNumericField(key, value)) continue
        const hasNumericValue =
            value !== null &&
            value !== undefined &&
            typeof value === "number" &&
            !Number.isNaN(value)
        let message = m.numericRequired
        if (
            hasNumericValue &&
            (key.includes("prix") || key.includes("prix_")) &&
            !isValidDecimalPlaces(value as number, 4)
        ) {
            message = m.decimalsInvalid
        }
        out.push(createError(`${pathPrefix}.${key}`, message))
    }
    return out
}

function collectSubscribeErrors(
    sub: PvEtudeEcoSubscribeForm | undefined,
    pathPrefix: "subscribe_before" | "subscribe_after",
    createError: CreateFieldError,
    m: PvEtudeEcoFormSchemaMessages
): yup.ValidationError[] {
    const out: yup.ValidationError[] = []
    if (!sub) return out
    const pt = sub.pricing_type
    const slice = sub[pt] as Record<string, unknown>
    for (const [key, value] of Object.entries(slice)) {
        if (key === "plage") {
            const plages = value as PvEtudeEcoPlageHoraire[]
            if (!Array.isArray(plages) || plages.length === 0) {
                out.push(
                    createError(
                        `${pathPrefix}.${pt}.plage`,
                        m.plageBlockInvalid
                    )
                )
                continue
            }
            plages.forEach((p, i) => {
                if (!p.time_from) {
                    out.push(
                        createError(
                            `${pathPrefix}.${pt}.plage.${i}.time_from`,
                            m.plageStartRequired
                        )
                    )
                }
                if (!p.time_to) {
                    out.push(
                        createError(
                            `${pathPrefix}.${pt}.plage.${i}.time_to`,
                            m.plageEndRequired
                        )
                    )
                }
            })
            continue
        }
        if (!isValidNumericField(key, value)) {
            const hasNumericValue =
                value !== null &&
                value !== undefined &&
                typeof value === "number" &&
                !Number.isNaN(value)
            let message = m.numericRequired
            if (
                hasNumericValue &&
                (key.includes("prix") || key.includes("prix_")) &&
                !isValidDecimalPlaces(value as number, 4)
            ) {
                message = m.decimalsInvalid
            }
            out.push(createError(`${pathPrefix}.${pt}.${key}`, message))
        }
    }
    return out
}

/**
 * Schéma Yup du formulaire complet (chemins d’erreurs alignés sur vee-validate).
 * Yup 1.x livré sans `superRefine` : validation croisée via `.test()` racine.
 */
export function createPvEtudeEcoFormSchema(m: PvEtudeEcoFormSchemaMessages) {
    return yup
        .object({
            name_analyse: yup
                .string()
                .required(m.nameRequired)
                .test(
                    "non-empty",
                    m.nameRequired,
                    (v) => (v ?? "").trim().length > 0
                )
                .test("aws-name", m.nameInvalid, (v) =>
                    isValidAwsString((v ?? "").trim())
                ),
            general_info: yup
                .mixed<PvEtudeEcoFullForm["general_info"]>()
                .required(),
            subscribe_before: yup
                .mixed<PvEtudeEcoFullForm["subscribe_before"]>()
                .required(),
            subscribe_after: yup
                .mixed<PvEtudeEcoFullForm["subscribe_after"]>()
                .required(),
            price: yup.mixed<PvEtudeEcoFullForm["price"]>().required(),
        })
        .test(
            "pv-eco-nested",
            m.numericRequired,
            function (form: PvEtudeEcoFullForm | undefined) {
                if (!form) return true
                const inner: yup.ValidationError[] = []
                const ce = (path: string, message: string) =>
                    this.createError({ path, message })

                inner.push(
                    ...collectNumericObjectErrors(
                        form.general_info as unknown as Record<string, unknown>,
                        "general_info",
                        ce,
                        m
                    ),
                    ...collectNumericObjectErrors(
                        form.price as unknown as Record<string, unknown>,
                        "price",
                        ce,
                        m
                    ),
                    ...collectSubscribeErrors(
                        form.subscribe_before,
                        "subscribe_before",
                        ce,
                        m
                    ),
                    ...collectSubscribeErrors(
                        form.subscribe_after,
                        "subscribe_after",
                        ce,
                        m
                    )
                )
                if (inner.length) {
                    throw new yup.ValidationError(inner, form, "")
                }
                return true
            }
        )
}

function mapYupValidationErrorsToPaths(
    err: yup.ValidationError
): Record<string, string> {
    const out: Record<string, string> = {}
    const normalizePath = (p: string): string =>
        p.replace(/\["(\d+)"\]/g, ".$1")

    if (err.inner?.length) {
        for (const i of err.inner) {
            const p = normalizePath(i.path || "")
            if (p) {
                out[p] = i.message
            }
        }
    }
    if (!Object.keys(out).length && err.path) {
        out[normalizePath(err.path)] = err.message
    }
    return out
}

/**
 * Valide le formulaire avec Yup (hors vee-validate) pour éviter la validation silencieuse au montage
 * et garder des chemins d’erreurs alignés sur `setErrors` / `errors` vee-validate.
 */
export async function validatePvEtudeEcoFormWithYup(
    form: PvEtudeEcoFullForm,
    messages: PvEtudeEcoFormSchemaMessages
): Promise<
    | { ok: true; values: PvEtudeEcoFullForm }
    | { ok: false; errors: Record<string, string> }
> {
    const schema = createPvEtudeEcoFormSchema(messages)
    try {
        const output = (await schema.validate(form, {
            abortEarly: false,
        })) as PvEtudeEcoFullForm
        return { ok: true, values: output }
    } catch (e: unknown) {
        if (e instanceof yup.ValidationError) {
            return { ok: false, errors: mapYupValidationErrorsToPaths(e) }
        }
        throw e
    }
}
